import { Admin } from '@/types/admin.types';
import { adminFileStorageService } from './admins/fileStorageService';

// Export functions with the names expected by other parts of the app
export const getAdmins = async (): Promise<Admin[]> => {
  try {
    console.log('📋 Getting all admins from localStorage with sequential IDs...');
    const admins = await adminFileStorageService.getAllAdmins();
    // Ensure we always return an array, never undefined or null
    return Array.isArray(admins) ? admins : [];
  } catch (error) {
    console.error('Error in getAdmins:', error);
    return [];
  }
};

export const getAllAdmins = async (): Promise<Admin[]> => {
  try {
    const admins = await getAdmins();
    // Double check that we return an array
    return Array.isArray(admins) ? admins : [];
  } catch (error) {
    console.error('Error in getAllAdmins:', error);
    return [];
  }
};

export const getAdminById = async (id: string): Promise<Admin | undefined> => {   
  try {
    console.log(`🔍 Getting admin by ID: ${id} from localStorage...`);
    // Support both formats: "#1" and "1"
    const numericId = id.replace('#', '');
    const admin = await adminFileStorageService.getAdminById(numericId);
    return admin || undefined;
  } catch (error) {
    console.error('Error in getAdminById:', error);
    return undefined;
  }
};

export const saveAdmin = async (admin: Admin): Promise<Admin> => {
  try {
    console.log('💾 Saving admin to localStorage with sequential ID...');
    return await adminFileStorageService.saveAdmin(admin);
  } catch (error) {
    console.error('Error in saveAdmin:', error);
    throw error;
  }
};

export const createAdmin = async (adminData: Omit<Admin, 'id' | 'createdAt' | 'updatedAt'>): Promise<Admin> => {
  try {
    console.log('👥 Creating admin using localStorage...');
    const now = new Date().toISOString();
    const adminToCreate: Admin = {
      ...adminData,
      id: `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: now,
      updatedAt: now
    } as Admin;
    return await adminFileStorageService.addAdmin(adminToCreate);
  } catch (error) {
    console.error('Error in createAdmin:', error);
    throw error;
  }
};

export const updateAdmin = async (id: string, updates: Partial<Admin>): Promise<Admin | null> => {
  try {
    console.log(`👥 Updating admin with ID: ${id} using localStorage...`);
    return await adminFileStorageService.updateAdmin(id, updates);
  } catch (error) {
    console.error('Error in updateAdmin:', error);
    throw error;
  }
};

export const deleteAdmin = async (id: string): Promise<boolean> => {
  try {
    console.log(`🗑️ Deleting admin ${id} from localStorage...`);
    await adminFileStorageService.deleteAdmin(id);
    return true;
  } catch (error) {
    console.error('Error in deleteAdmin:', error);
    return false;
  }
};

export const clearAllAdminData = async (): Promise<void> => {
  try {
    console.log('👥 Clearing all admin data from localStorage...');
    localStorage.setItem('nalc_admins', JSON.stringify([]));
    adminFileStorageService.clearCache();
    console.log('✅ All admin data cleared from localStorage successfully');
  } catch (error) {
    console.error('Failed to clear admin data from localStorage:', error);
    throw error;
  }
};

export const addAdminHistory = async (adminId: string, action: string, details: string): Promise<void> => {
  try {
    const admin = await getAdminById(adminId);
    if (admin) {
      const historyEntry = {
        id: `history_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        action,
        date: new Date().toISOString(),
        details
      };
      
      admin.history = admin.history || [];
      admin.history.unshift(historyEntry);
      
      await saveAdmin(admin);
      console.log(`History added for admin ${adminId}: ${action}`);
    }
  } catch (error) {
    console.error('Error adding admin history:', error);
  }
};

export const getAdminStorageStats = async () => {
  try {
    return await adminFileStorageService.getStorageStats();
  } catch (error) {
    console.error('Error getting admin storage stats:', error);
    return {
      totalAdmins: 0,
      storageUsed: '0 KB',
      maxCapacity: 5000,
      isOptimized: false
    };
  }
};
